import { z } from "zod";
import { doodleAnimateSchema } from "./theme-style.js";
import { previewUrlSchema, imageSizeSchema, previewUrlWithSizeSchema, } from "./preview-url.js";
import { transitionSchema } from "./transition.js";
const meadowFlowersBeeSchema = z.object({
    src: previewUrlWithSizeSchema,
    sizeScale: z.number().optional(),
    // The position offset from positionPercent will be multiplied by this number.
    // Set to less than 1 if you want the movement range to be smaller. Set to
    // larger than one if you want the movement range to be larger.
    movementScale: z.number().optional(),
    // The position as a percentage value of the screen size (e.g. `50` ===
    // `50%`)
    positionPercent: z.object({
        x: z.number(),
        y: z.number(),
    }),
    enter: z.object({
        start: z.object({
            frame: z.number(),
            blur: z.number().optional(),
            opacity: z.number().optional(),
        }),
        end: z.object({
            frame: z.number(),
            blur: z.number().optional(),
            opacity: z.number().optional(),
        }),
    }),
});
const infiniteScrollImageSchema = z.object({
    src: previewUrlWithSizeSchema,
    // If you want the image to be larger or smaller than the full-size image. You
    // can achieve the same effect by changing the width and height, but this is
    // easier.
    scale: z.number().optional(),
    enter: z
        .object({
        start: z.object({
            frame: z.number(),
            blur: z.number().optional(),
            opacity: z.number().optional(),
            position: z
                .object({
                x: z.object({
                    // where to measure the position from
                    from: z.enum(["left", "right"]),
                    offset: z.number(),
                }),
                y: z.object({
                    from: z.enum(["top", "bottom"]),
                    offset: z.number(),
                }),
            })
                .optional(),
        }),
        end: z.object({
            frame: z.number(),
            blur: z.number().optional(),
            opacity: z.number().optional(),
        }),
    })
        .optional(),
    repeat: z.enum(["repeat", "repeat-x", "repeat-y"]),
    // will move the image this many pixels per second
    movePerSecond: z.object({
        // this is a pixel value
        x: z.number(),
        y: z.number(),
    }),
});
const backgroundDoodleImageSchema = z.object({
    // Size is necessary so that the size of the optimized preview image matches
    // the full-size image.
    src: previewUrlWithSizeSchema,
    // override the size of the image
    displaySize: imageSizeSchema.optional(),
    enter: z.object({
        start: z.object({
            frame: z.number(),
            position: z.object({
                // these are percentages (e.g. {x: 50, y: 50} is (50%, 50%))
                x: z.number(),
                y: z.number(),
            }),
            rotation: z.number(),
            scale: z.number(),
        }),
        end: z.object({
            frame: z.number(),
            position: z
                .object({
                // these are percentages (e.g. {x: 50, y: 50} is (50%, 50%))
                x: z.number(),
                y: z.number(),
            })
                .optional(),
            rotation: z.number().optional(),
            scale: z
                .union([z.number(), z.object({ value: z.number(), back: z.number() })])
                .optional(),
            back: z.number().optional(),
        }),
    }),
    animate: doodleAnimateSchema.optional(),
});
const imageBackgroundSchema = z.object({
    variant: z.literal("image"),
    src: previewUrlSchema,
    color: z.string().optional(),
    decorativeImages: z
        .discriminatedUnion("variant", [
        z.object({
            variant: z.literal("left-full-height"),
            url: previewUrlSchema,
        }),
        z.object({
            variant: z.literal("right-full-height"),
            url: previewUrlSchema,
        }),
    ])
        .array()
        .optional(),
});
export const popHeartsBackgroundImageSchema = z.object({
    src: previewUrlWithSizeSchema,
    // NOTE: These are all percentages
    // The hearts start in this position.
    topStart: z.number(),
    leftStart: z.number(),
    // The hearts will pop up into this position after the entrance animation. The
    // idea is that the hearts will pop "away" from the start position.
    topPopped: z.number(),
    leftPopped: z.number(),
    scale: z.number().optional(),
});
const colorSeriesBackgroundSchema = z.object({
    variant: z.literal("color-series"),
    colors: z.string().array().min(1),
    // Skip this many sub-scenes before starting to show the colors. See
    // getSubSceneSkipToFrames for an explanation of what a sub-scene is.
    skipNumSubScenes: z.number().optional(),
    transition: transitionSchema,
});
export const backgroundSchema = z
    .discriminatedUnion("variant", [
    z.object({
        variant: z.literal("color"),
        color: z.string(),
    }),
    z.object({
        variant: z.literal("themed-color"),
    }),
    z.object({
        variant: z.literal("rive"),
        src: z.string(),
    }),
    z.object({
        variant: z.literal("lottie"),
        color: z.string().optional(),
        src: z.string(),
    }),
    imageBackgroundSchema,
    z.object({
        variant: z.literal("confetti"),
        colors: z.string().array().optional(),
        background: z.string().optional(),
        count: z.number().optional(),
    }),
    z.object({
        variant: z.literal("gif"),
        src: z.string(),
    }),
    z.object({
        variant: z.literal("image-sequence"),
        // Use {{i}} for the image number. Image numbers start at 0. For example:
        // http://localhost:3000/images/sequence/image--{{i}}.png
        srcUrlTemplate: z.string(),
        // If the image numbers are zero padded, specify the number of digits here.
        // E.g. `image-0001.png` would be `4`
        srcUrlTemplateZeroPad: z.number().optional(),
        numImages: z.number(),
        // the number of images (frames) of the sequence to display per second
        imagesPerSecond: z.number(),
    }),
    z.object({
        variant: z.literal("geometric-blend"),
        src: z.string(),
    }),
    z.object({
        variant: z.literal("pop-hearts"),
        src: previewUrlSchema,
        // When to make the hearts "pop"
        popAtSeconds: z.number(),
        // When to "un-pop" (return to start position)
        restoreAtSeconds: z.number(),
        popAnimationDurationSeconds: z.number(),
        hearts: popHeartsBackgroundImageSchema.array(),
    }),
    z.object({
        variant: z.literal("diamonds"),
        urls: z.string().array(),
        blendMode: z.enum(["invert-overlay", "opacity"]).optional(),
        backgroundColor: z.string().optional(),
        style: z
            .object({
            backgroundSize: z.string().optional(),
            opacity: z.number().optional(),
        })
            .optional(),
    }),
    z.object({
        variant: z.literal("scattered"),
        urls: previewUrlSchema.array(),
        count: z.number(),
        adjustTimeSeconds: z.number(),
    }),
    z.object({
        variant: z.literal("looped-video"),
        src: z.string(),
    }),
    z.object({
        variant: z.literal("css-gradient"),
    }),
    z.object({
        variant: z.literal("shimmer"),
        images: previewUrlSchema.array(),
    }),
    z.object({
        variant: z.literal("doodle"),
        images: backgroundDoodleImageSchema.array(),
    }),
    z.object({
        variant: z.literal("infinite-scroll"),
        images: infiniteScrollImageSchema.array(),
        color: z.string().optional(),
    }),
    z.object({
        variant: z.literal("meadow-flowers"),
        images: infiniteScrollImageSchema.array(),
        bees: meadowFlowersBeeSchema.array(),
        color: z.string(),
    }),
    colorSeriesBackgroundSchema,
    z.object({
        variant: z.literal("big-congrats"),
        // how long each color lasts
        colorDurationSeconds: z.number(),
        colors: z.string().array(),
        borderImage: previewUrlSchema,
        // These images sort of flow/fade into the center
        flowImages: previewUrlSchema.array(),
    }),
    z.object({
        variant: z.literal("none"),
    }),
])
    .superRefine((data, ctx) => {
    if (data.variant === "pop-hearts") {
        if (data.popAtSeconds >= data.restoreAtSeconds) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "popAtSeconds must be less than restoreAtSeconds",
                path: ["popAtSeconds"],
            });
        }
        if (data.popAtSeconds - data.popAnimationDurationSeconds <= 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "popAtSeconds must be longer than popAnimationDurationSeconds",
                path: ["popAtSeconds"],
            });
        }
        if (data.restoreAtSeconds - data.popAnimationDurationSeconds <=
            data.popAtSeconds) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "restoreAtSeconds must be after (popAtSeconds + popAnimationDurationSeconds)in order to interpolate the restore animation",
                path: ["restoreAtSeconds"],
            });
        }
    }
});
